<template>
  <div class="flex items-center space-x-4 py-4 border-b border-gray-200 last:border-b-0">
    <!-- Product Image -->
    <div class="flex-shrink-0 w-16 h-16 bg-gray-100 rounded-md overflow-hidden">
      <img 
        :src="getThumbnailImage()" 
        :alt="item.product.name"
        class="w-full h-full object-cover"
        @error="handleImageError"
      />
    </div>

    <!-- Product Info -->
    <div class="flex-1 min-w-0">
      <h4 class="text-sm font-medium text-gray-900 truncate" v-html="item.product.name">
      </h4>
      <p class="text-sm text-gray-500">
        {{ formatPrice(getItemPrice()) }} {{ $t('pro Stück') }}
      </p>
      <p class="text-sm font-medium text-gray-900">
        {{ formatPrice(getTotalPrice()) }}
      </p>
    </div>

    <!-- Quantity Controls -->
    <div class="flex items-center space-x-2">
      <button
        @click="decreaseQuantity"
        :disabled="item.quantity <= 0"
        class="w-6 h-6 rounded-full bg-gray-100 hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center text-gray-600 hover:text-gray-800"
      >
        <svg class="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
          <path fill-rule="evenodd" d="M3 10a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1z" clip-rule="evenodd"/>
        </svg>
      </button>
      
      <span class="text-sm font-medium w-8 text-center">
        {{ item.quantity }}
      </span>
      
      <button
        @click="increaseQuantity"
        class="w-6 h-6 rounded-full bg-gray-100 hover:bg-gray-200 flex items-center justify-center text-gray-600 hover:text-gray-800"
      >
        <svg class="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
          <path fill-rule="evenodd" d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z" clip-rule="evenodd"/>
        </svg>
      </button>
    </div>

    <!-- Remove Button -->
    <button
      @click="removeItem"
      class="text-red-500 hover:text-red-700 transition-colors"
    >
      <svg class="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
        <path fill-rule="evenodd" d="M9 2a1 1 0 000 2h2a1 1 0 100-2H9z" clip-rule="evenodd"/>
        <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clip-rule="evenodd"/>
      </svg>
    </button>
  </div>
</template>

<script setup lang="ts">
import type { CartItem } from '@/types/cart'

interface Props {
  item: CartItem
}

interface Emits {
  (e: 'update-quantity', itemId: string, quantity: number): void
  (e: 'remove', itemId: string): void
}

const props = defineProps<Props>()
const emit = defineEmits<Emits>()

const getItemPrice = (): number => {
  // Use the cart store's tier-based pricing calculation
  return props.item.price
}

const getTotalPrice = (): number => {
  return getItemPrice() * props.item.quantity
}

const formatPrice = (price: number): string => {
  return new Intl.NumberFormat('de-DE', {
    style: 'currency',
    currency: props.item.product.currency || 'EUR'
  }).format(price)
}

const increaseQuantity = () => {
  emit('update-quantity', props.item.id, props.item.quantity + 1)
}

const decreaseQuantity = () => {
  if (props.item.quantity > 0) {
    emit('update-quantity', props.item.id, props.item.quantity - 1)
  }
}

const removeItem = () => {
  emit('remove', props.item.id)
}

const getThumbnailImage = (): string => {
  const image = props.item.product.image
  return image.endsWith('.jpg') 
    ? image.replace('.jpg', '_thumb.jpg')
    : image + '_thumb.jpg'
}

const handleImageError = (event: Event) => {
  const target = event.target as HTMLImageElement
  // Fallback to regular image without _thumb suffix if thumbnail fails
  if (target.src.includes('_thumb.jpg')) {
    target.src = target.src.replace('_thumb.jpg', '.jpg')
  }
}
</script>